// utils/urlUtils.ts

import { API_CONFIG } from "@/api/config";

// This file used to define its own duplicate `cn` (identical clsx+twMerge
// implementation to './cn') — re-exporting the one canonical definition
// instead so `export * from './cn'` and `export * from './urlUtils'` in
// utils/index.ts resolve to the same binding instead of colliding.
export { cn } from './cn';
/**
 * Removes trailing '/api' or '/api/' from a URL, but only if it's at the very end
 * Example: 
 * - 'http://10.0.2.2:5062/api' → 'http://10.0.2.2:5062/'
 * - 'https://api.prashrestha.com.np/api/' → 'https://api.prashrestha.com.np/'
 * - 'http://example.com/v1/api/users' → 'http://example.com/v1/api/users' (unchanged, '/api' not at end)
 */
export const removeTrailingApi = (baseUrl: string): string => {
  if (!baseUrl || typeof baseUrl !== 'string') {
    return baseUrl || '';
  }

  try {
    const urlObj = new URL(baseUrl);
    let pathname = urlObj.pathname;
    
    // Remove only '/api' or '/api/' from the VERY END of the pathname
    if (pathname.endsWith('/api/')) {
      // Remove '/api/' from the end
      pathname = pathname.substring(0, pathname.length - 5);
    } else if (pathname.endsWith('/api')) {
      // Remove '/api' from the end
      pathname = pathname.substring(0, pathname.length - 4);
    }
    
    // Ensure pathname starts with '/'
    if (pathname === '') {
      pathname = '/';
    } else if (!pathname.startsWith('/')) {
      pathname = '/' + pathname;
    }
    
    // Ensure pathname ends with '/' if it's not empty and not just '/'
    if (pathname !== '/' && !pathname.endsWith('/')) {
      pathname += '/';
    }
    
    urlObj.pathname = pathname;
    return urlObj.toString();
    
  } catch (error) {
    // Fallback regex approach for invalid URLs
    // Only removes '/api' or '/api/' at the very end of the string
    return baseUrl.replace(/\/api\/?$/, '');
  }
};


export const getAbsoluteUrl = (url: string): string => {
  if (!url) return '';
  
  // If it's already an absolute URL, return as is
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
    return url;
  }
  
  // If it's a base64 data URL, return as is
  if (url.startsWith('data:')) {
    return url;
  }
  
  // Prepend base URL if it's a relative path
  const baseUrl = process.env.NEXT_PUBLIC_API_URL || '';
  return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};

/**
 * Converts a relative URL to an absolute URL, handling the API base URL properly
 * Example: '/uploads/image.jpg' → 'http://10.0.2.2:5062/uploads/image.jpg'
 */
export const convertToAbsoluteUrl = (relativeUrl: string): string => {
  if (!relativeUrl || typeof relativeUrl !== 'string') {
    return relativeUrl || '';
  }
  
  // If URL is already absolute, return as-is
  if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
    return relativeUrl;
  }
  
  // Remove trailing '/api' from base URL
  const baseUrlWithoutApi = removeTrailingApi(API_CONFIG.baseURL);
  
  // Ensure base URL ends with '/'
  let baseUrl = baseUrlWithoutApi;
  if (!baseUrl.endsWith('/')) {
    baseUrl += '/';
  }
  
  // Remove leading '/' from relative URL if present
  let urlPath = relativeUrl;
  if (urlPath.startsWith('/')) {
    urlPath = urlPath.substring(1);
  }
  
  return `${baseUrl}${urlPath}`;
};

// Optional: Get media URL with fallback for thumbnails
export const getMediaUrl = (url: string, thumbnail = false): string => {
  if (!url) return '';
  
  const absoluteUrl = convertToAbsoluteUrl(url);
  
  if (thumbnail) {
    // If you want to generate thumbnail URLs, you can add logic here
    // Example: Append thumbnail parameter or use a different endpoint
    return absoluteUrl;
  }
  
  return absoluteUrl;
};